import { retryWithBackoff, isRetryableError, getRateLimitRetryDelayMs } from '../../utils/retry'
import { ERROR_MESSAGES } from '../../constants/errorMessages'
import { logger } from '../../utils/logger'
import { config } from '../../config'
import { maybeCaptureApiError } from '../../features/bug-report/captureApiError'
import { maybeReportAutoBugFromApiError } from '../../features/bug-report/autoReporter'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const { api: apiConfig, auth: authConfig } = config
const tagpFetch = createServiceFetch(apiConfig.baseUrl)

export class ApiError extends Error {
  status: number
  code: string
  requestId: string
  data?: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = ''
    this.requestId = ''
    this.data = data
  }
}

/**
 * Extract a human-readable error message from the backend response.
 * Supports both the unified nested schema {"error": {"message": ...}}
 * and the legacy flat schema {"error": "...", "detail": "..."}.
 */
/**
 * Some backend errors arrive as a Python-repr dict embedded in the message,
 * e.g. `"409: {'error': \"Skill ... already exists\", 'error_code': 'CONFLICT', 'status_code': 409}"`.
 * Extract the inner `error` string when present; otherwise return the input unchanged.
 */
function unwrapEmbeddedBackendMessage(message: string): string {
  const match = message.match(/^\s*\d{3}:\s*\{.*'error'\s*:\s*(?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)')/)
  if (match) {
    const raw = match[1] ?? match[2] ?? ''
    return raw.replace(/\\(["'\\])/g, '$1')
  }
  return message
}

const VALIDATION_FIELD_LABELS: Record<string, string> = {
  dataverse_base_url: 'Organization URL',
  dataverse_access_token: 'Dataverse access token',
  bot_id: 'Bot to import',
}

export function extractErrorMessage(errorData: Record<string, unknown>, fallback: string): string {
  const nested = errorData?.error
  if (nested && typeof nested === 'object') {
    const nestedObj = nested as Record<string, unknown>
    if ('message' in nestedObj && typeof nestedObj.message === 'string') {
      return unwrapEmbeddedBackendMessage(nestedObj.message)
    }
    if (nestedObj.message && typeof nestedObj.message === 'object') {
      const inner = nestedObj.message as Record<string, unknown>
      if (typeof inner.message === 'string') {
        return inner.message
      }
    }
  }
  if (typeof errorData?.error === 'string') return unwrapEmbeddedBackendMessage(errorData.error)
  if (typeof errorData?.detail === 'string') return unwrapEmbeddedBackendMessage(errorData.detail)

  // Handle FastAPI array validations (422) where detail is an array
  if (Array.isArray(errorData?.detail)) {
    try {
      const details = errorData.detail as Array<{
        loc?: unknown[]
        msg?: string
        type?: string
        ctx?: { min_length?: number; max_length?: number }
      }>
      if (details.length > 0 && typeof details[0] === 'object' && details[0] && 'msg' in details[0]) {
        return details.map((d) => {
          const field = Array.isArray(d.loc)
            ? [...d.loc].reverse().find((x): x is string => typeof x === 'string' && x !== 'body')
            : undefined
          const label = (field && VALIDATION_FIELD_LABELS[field]) || (field ? field.replace(/_/g, ' ') : 'Field')
          if (d.type === 'string_too_short' && d.ctx?.min_length) {
            return `${label} must be at least ${d.ctx.min_length} characters`
          }
          if (d.type === 'string_too_long' && d.ctx?.max_length) {
            return `${label} must be no more than ${d.ctx.max_length} characters`
          }
          if (d.type === 'missing') return `${label} is required`
          return `${label}: ${d.msg}`
        }).join('. ')
      }
      return JSON.stringify(errorData.detail)
    } catch {
      return JSON.stringify(errorData.detail)
    }
  }

  // Handle structured error object in detail (e.g. FastAPI { code, message })
  if (errorData?.detail && typeof errorData.detail === 'object' && !Array.isArray(errorData.detail)) {
    const d = errorData.detail as Record<string, unknown>
    if (typeof d.message === 'string' && d.message.trim()) {
      return unwrapEmbeddedBackendMessage(d.message.trim())
    }
    // StandardHTTPException / ForbiddenError: { error, error_code, status_code }
    if (typeof d.error === 'string' && d.error.trim()) {
      return unwrapEmbeddedBackendMessage(d.error.trim())
    }
    return JSON.stringify(errorData.detail)
  }

  // Fallback for an object in 'error'
  if (errorData?.error && typeof errorData.error === 'object') {
     return JSON.stringify(errorData.error)
  }

  return fallback
}

/**
 * Build an ApiError from parsed backend error data, populating
 * structured fields (code, requestId) when the unified envelope is present.
 */
function buildApiError(errorData: Record<string, unknown>, status: number, fallbackMessage: string): ApiError {
  const message = extractErrorMessage(errorData, fallbackMessage)
  const apiError = new ApiError(message, status, errorData)

  // TAGP flat envelope: { error, error_code, status_code }
  if (typeof errorData?.error_code === 'string' && errorData.error_code) {
    apiError.code = errorData.error_code
  }

  const nested = errorData?.error
  if (nested && typeof nested === 'object') {
    const ne = nested as Record<string, unknown>
    if (!apiError.code) {
      apiError.code = (ne.code as string) || ''
    }
    apiError.requestId = (ne.request_id as string) || ''
  }

  return apiError
}

/**
 * Build an ApiError from a non-JSON `request()` response body (e.g. raw `fetch` + `response.text()`).
 * Parses JSON when possible; otherwise uses the body as the message.
 */
export function apiErrorFromResponseBody(
  status: number,
  bodyText: string,
  fallbackMessage: string
): ApiError {
  const trimmed = (bodyText ?? '').trim()
  if (!trimmed) {
    return new ApiError(fallbackMessage, status)
  }
  try {
    const data = JSON.parse(trimmed) as unknown
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return buildApiError(data as Record<string, unknown>, status, fallbackMessage)
    }
  } catch {
    // Non-JSON error page or plain text
  }
  return new ApiError(trimmed, status)
}

// Token refresh state management
let isRefreshing = false
let refreshPromise: Promise<string> | null = null

/**
 * Decode a JWT payload without verification (client-side only) and check
 * whether the token will expire within `bufferMs` milliseconds.
 * Returns true when the token is missing, malformed, or about to expire
 * so the caller can trigger a proactive refresh.
 */
export function isTokenExpiringSoon(token: string | null, bufferMs = 60_000): boolean {
  if (!token) return true
  try {
    const base64url = token.split('.')[1]
    if (!base64url) return true
    // JWT uses base64url without padding; atob requires standard base64 + padding.
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const payload = JSON.parse(atob(padded))
    const expMs = (payload.exp as number) * 1000
    return expMs - Date.now() < bufferMs
  } catch {
    return true
  }
}

/**
 * Refresh the access token using the refresh token
 */
export async function refreshAccessToken(): Promise<string> {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = (async () => {
    let refreshToken: string | null = null
    try {
      // Safely get refresh token from localStorage
      try {
        refreshToken = localStorage.getItem(authConfig.refreshTokenKey)
      } catch (storageError) {
        logger.error('Failed to read refresh token from localStorage', storageError)
        throw new ApiError('Failed to access refresh token', 401)
      }

      if (!refreshToken || refreshToken.trim() === '') {
        logger.debug('No refresh token in localStorage; trying HttpOnly session cookie')
        const cookieRefresh = await tagpFetch(`${apiConfig.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: '' }),
          credentials: 'include',
        })
        if (cookieRefresh.ok) {
          const data = await cookieRefresh.json()
          const newAccessToken = data.access_token as string
          if (newAccessToken && typeof newAccessToken === 'string') {
            try {
              localStorage.setItem(authConfig.tokenKey, newAccessToken)
            } catch (storageError) {
              logger.error('Failed to store refreshed access token', storageError)
            }
            return newAccessToken
          }
        }
        // Cookie session refresh failed. A 401 means the Redis session was revoked
        // (idle timeout, logout elsewhere, etc.) — treat like any other refresh 401.
        // Non-401 failures may be transient infra issues; keep the session alive.
        if (cookieRefresh.status === 401) {
          logger.warn('Cookie session refresh rejected (session expired or idle)', {
            cookieStatus: cookieRefresh.status,
          })
          try {
            localStorage.removeItem(authConfig.tokenKey)
            localStorage.removeItem(authConfig.refreshTokenKey)
          } catch (storageError) {
            logger.error('Failed to clear tokens from localStorage', storageError)
          }
          window.dispatchEvent(new CustomEvent('auth:token-expired'))
          throw new ApiError('Session expired', 401)
        }
        logger.warn('No refresh token available and cookie session refresh failed', {
          cookieStatus: cookieRefresh.status,
        })
        throw new ApiError('No refresh token available', 401)
      }

      const response = await tagpFetch(`${apiConfig.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = extractErrorMessage(errorData, 'Token refresh failed')

        logger.warn('Token refresh failed', {
          status: response.status,
          error: errorMessage,
          errorCode: errorData?.error_code,
        })

        // Synchronously clear both the access token and the refresh token to immediately
        // trigger unauthentication across all components and prevent any cascading requests.
        if (response.status === 401) {
          try {
            localStorage.removeItem(authConfig.tokenKey)
            localStorage.removeItem(authConfig.refreshTokenKey)
          } catch (storageError) {
            logger.error('Failed to clear tokens from localStorage', storageError)
          }
          window.dispatchEvent(new CustomEvent('auth:token-expired'))
        }

        throw buildApiError(errorData, response.status, 'Token refresh failed')
      }

      const data = await response.json()
      const newAccessToken = data.access_token

      if (!newAccessToken || typeof newAccessToken !== 'string') {
        logger.error('Invalid access token received from refresh endpoint', { data })
        throw new ApiError('Invalid access token received', 500)
      }

      // Store new access token safely
      try {
        localStorage.setItem(authConfig.tokenKey, newAccessToken)
        logger.debug('Access token refreshed and stored successfully')
      } catch (storageError) {
        logger.error('Failed to store refreshed access token', storageError)
        // Don't throw - token refresh succeeded, storage failure is recoverable
        // User will need to refresh page or tokens will be lost on next refresh
      }

      return newAccessToken
    } catch (error) {
      // If it's already an ApiError, re-throw it
      if (error instanceof ApiError) {
        throw error
      }

      // Unexpected errors (network timeouts, DNS failures, CORS, etc.) — do NOT
      // clear tokens or dispatch auth:token-expired.  Nuking localStorage
      // here causes cascading 401s for concurrent Agentserve/API calls that
      // fall back to the cached access token.  Callers decide what to do.
      // Logged at warn (not error) because "Failed to fetch" during a background
      // proactive refresh is a transient network/infra event, not a product bug.
      logger.warn('Unexpected error during token refresh', {
        message: error instanceof Error ? error.message : String(error),
      })

      throw new ApiError('Token refresh failed due to unexpected error', 500, {
        originalError: error instanceof Error ? error.message : String(error),
      })
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export interface RequestOptions extends RequestInit {
  /** AbortSignal for request cancellation */
  signal?: AbortSignal
  /**
   * Platform admin: one-shot tenant for this request (`X-Tenant-ID`).
   * When set, overrides the header tenant switcher without changing global selection.
   */
  tenantId?: string
  /**
   * Whether to retry on failure (default: false).
   * Default is false because React Query handles retries for queries.
   * Set to true for non-React-Query requests that need retry behavior.
   */
  retry?: boolean
  /**
   * Whether a 401 response should trigger the global token-refresh / session-expiry
   * (logout + redirect to login) flow (default: true).
   *
   * Set to false for endpoints that proxy a downstream call and mirror the
   * downstream auth status (e.g. the tool execution proxy). For those a 401 means
   * "the downstream/target credential failed", not "the TAGP session expired", so
   * it must be surfaced as a normal response instead of logging the user out.
   */
  retryOn401?: boolean
}

/**
 * Internal function to make the actual request
 */
async function makeRequest<T>(
  url: string,
  config: RequestInit,
  retryOn401: boolean = true
): Promise<T> {
  try {
    const response = await tagpFetch(url, config)

    // Handle 401 Unauthorized - try to refresh token
    if (response.status === 401 && retryOn401) {
      // Don't retry auth endpoints - login failures are not token expiration issues
      if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
        const errorData = await response.json().catch(() => ({}))
        if (url.includes('/auth/refresh')) {
          logger.warn('Auth refresh endpoint returned 401', { url, errorData })
        }
        throw buildApiError(errorData, response.status, ERROR_MESSAGES.AUTHENTICATION_FAILED)
      }

      logger.debug('Received 401, attempting token refresh', { url })
      try {
        // Attempt to refresh the token
        const newAccessToken = await refreshAccessToken()
        logger.debug('Token refresh successful, retrying request', { url })

        // Retry the original request with new token
        const retryConfig = {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        }

        const retryResponse = await tagpFetch(url, retryConfig)

        if (!retryResponse.ok) {
          if (retryResponse.status === 401) {
            // Fresh token still rejected — session is truly dead.
            try {
              localStorage.removeItem(authConfig.tokenKey)
              localStorage.removeItem(authConfig.refreshTokenKey)
            } catch (storageError) {
              logger.error('Failed to clear tokens from localStorage', storageError)
            }
            window.dispatchEvent(new CustomEvent('auth:token-expired'))
          }

          const errorData = await retryResponse.json().catch(() => ({}))
          throw buildApiError(errorData, retryResponse.status, ERROR_MESSAGES.SOMETHING_WENT_WRONG)
        }

        // Handle 204 No Content
        if (retryResponse.status === 204) {
          return undefined as T
        }

        // Handle empty responses safely
        const text = await retryResponse.text()
        if (!text || text.trim() === '') {
          return undefined as T
        }

        try {
          return JSON.parse(text) as T
        } catch (parseError) {
          throw new ApiError('Invalid response format from server', retryResponse.status, {
            parseError,
            text: text.substring(0, 100),
          })
        }
      } catch (refreshError) {
        // Refresh failed — don't clear the access token from localStorage.
        // It may still be useful for Agentserve or other services that
        // accept it.  The refreshAccessToken() function already dispatches
        // auth:token-expired when appropriate (e.g. 401 from refresh endpoint).
        logger.warn('Token refresh failed', refreshError)

        const errorData = await response.json().catch(() => ({}))
        throw buildApiError(errorData, 401, ERROR_MESSAGES.AUTHENTICATION_FAILED)
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))

      if (response.status === 403) {
        const errorMessage = extractErrorMessage(errorData, ERROR_MESSAGES.FORBIDDEN_ERROR)

        if (errorMessage.toLowerCase().includes('cancelled')) {
          window.dispatchEvent(new CustomEvent('auth:tenant-cancelled', {
            detail: { message: errorMessage }
          }))
        }

        throw buildApiError(errorData, response.status, ERROR_MESSAGES.FORBIDDEN_ERROR)
      }

      throw buildApiError(errorData, response.status, ERROR_MESSAGES.SOMETHING_WENT_WRONG)
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T
    }

    // Handle empty responses safely
    const text = await response.text()
    if (!text || text.trim() === '') {
      return undefined as T
    }

    try {
      return JSON.parse(text) as T
    } catch (parseError) {
      throw new ApiError('Invalid response format from server', response.status, {
        parseError,
        text: text.substring(0, 100),
      })
    }
  } catch (error) {
    // Don't wrap AbortError
    if (error instanceof Error && error.name === 'AbortError') {
      throw error
    }

    maybeCaptureApiError(url, error)
    maybeReportAutoBugFromApiError(url, error)

    if (error instanceof ApiError) {
      throw error
    }
    // Check if it's a network error (no response from server)
    const errorMessage = error instanceof Error ? error.message : String(error)
    const isNetworkError =
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('Network error') ||
      errorMessage.includes('ERR_NETWORK') ||
      errorMessage.includes('ERR_INTERNET_DISCONNECTED') ||
      errorMessage.includes('Network request failed') ||
      (error instanceof TypeError && errorMessage.includes('fetch'))

    if (isNetworkError) {
      throw new ApiError(ERROR_MESSAGES.NETWORK_ERROR, 0, error)
    }

    // For other errors, use generic message
    throw new ApiError(ERROR_MESSAGES.GENERIC, 0, error)
  }
}

/**
 * Get assumed role ID for API calls (admin "Switch Role" feature).
 * Reads from localStorage to avoid circular dependency with AssumedRoleContext.
 */
function getAssumedRoleIdForRequest(): string | null {
  try {
    const tenantId = localStorage.getItem('tagp-tenant-id')
    const key = tenantId
      ? `tagp-assumed-role-${tenantId}`
      : 'tagp-assumed-role'
    const stored = localStorage.getItem(key)
    if (stored) {
      const parsed = JSON.parse(stored)
      return parsed?.id || null
    }
  } catch {
    // ignore
  }
  return null
}

// Platform-admin tenant override for X-Tenant-ID. Registered synchronously by
// TenantProvider on mount (avoids async dynamic import missing the first requests).
let _overrideFn: (() => string | null) | null = null

/** Called from TenantProvider so authenticated admin API calls send X-Tenant-ID immediately. */
export function registerPlatformAdminOverrideTenantId(fn: () => string | null): void {
  _overrideFn = fn
}

function _getPlatformAdminOverrideTenantId(): string | null {
  if (_overrideFn) {
    const id = _overrideFn()
    if (id) return id
  }
  // Fallback until TenantProvider registers (or after hard refresh)
  try {
    return localStorage.getItem('tagp-tenant-id')
  } catch {
    return null
  }
}

/**
 * Get current tenant ID for API calls.
 * Imported from TenantContext to avoid circular dependencies.
 */
export function getTenantIdForRequest(): string | null {
  // Try to get from TenantContext's exported function
  try {
    // Dynamic import to avoid circular dependency
    // In production, TenantContext sets this via the exported getCurrentTenantId
    const tenantId = localStorage.getItem('tagp-tenant-id')
    if (tenantId) {
      return tenantId
    }

    // Try subdomain
    const hostname = window.location.hostname
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      const parts = hostname.split('.')
      if (parts.length >= 3) {
        const subdomain = parts[0]
        const reserved = ['www', 'api', 'admin', 'app', 'mail', 'ftp', 'static', 'assets']
        if (!reserved.includes(subdomain.toLowerCase())) {
          return subdomain.toLowerCase()
        }
      }
    }
  } catch {
    // Ignore errors
  }
  return null
}

export async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const url = `${apiConfig.baseUrl}${endpoint}`

  // Get JWT token from localStorage safely
  let token: string | null = null
  try {
    token = localStorage.getItem(authConfig.tokenKey)
  } catch (storageError) {
    logger.error('Failed to read token from localStorage', storageError)
    // Continue without token - request will fail with 401 if auth is required
  }

  // X-Tenant-ID header:
  //  - Unauthenticated: from localStorage/subdomain (registration, public).
  //  - Authenticated platform admin impersonation: from TenantContext override
  //    (only when admin has switched to a different tenant).
  //  - Regular authenticated: not sent (backend uses user's DB tenant).
  //  - Optional per-request tenantId in RequestOptions overrides the switcher.
  // Get assumed role ID for X-Assume-Role-Id header (admin role switching)
  const assumedRoleId = getAssumedRoleIdForRequest()

  // Log if token is missing for debugging (but only for non-auth endpoints and non-public endpoints)
  // Public endpoints that don't require auth: themes, health checks
  // Settings endpoints have localStorage fallbacks, so warnings are less critical
  const isPublicEndpoint =
    (endpoint.includes('/auth/') && !endpoint.includes('/auth/logout') && !endpoint.includes('/auth/me')) ||
    endpoint.includes('/themes/') ||
    endpoint.includes('/health') ||
    endpoint.includes('/admin/tenants/public') ||
    endpoint.includes('/admin/users/request')

  const hasFallback = endpoint.includes('/settings/') // Settings endpoints have localStorage fallbacks

  if (!token && !isPublicEndpoint && !hasFallback) {
    logger.warn('No authentication token found for request', { endpoint })
    throw new ApiError('No authentication token found', 401)
  }


  // Proactively refresh the access token before it expires so we avoid
  // a round-trip 401 + retry. Skip auth endpoints to prevent loops.
  if (token && !isPublicEndpoint && isTokenExpiringSoon(token)) {
    try {
      logger.debug('Access token expiring soon, proactively refreshing', { endpoint })
      const newToken = await refreshAccessToken()
      token = newToken
    } catch (_refreshErr) {
      logger.warn('Proactive token refresh failed, proceeding with current token', { endpoint })
    }
  }

  // Default retry to false - React Query handles retries for queries.
  // This prevents double-retry (API layer + React Query) which can cause
  // excessive requests, especially problematic with rate limiting (429).
  // Explicit retry: true can be passed for non-React-Query requests (e.g., mutations, streaming).
  const { retry: shouldRetry = false, retryOn401 = true, headers: customHeaders, tenantId: requestTenantId, ...restOptions } = options

  const tenantId =
    requestTenantId !== undefined
      ? requestTenantId || null
      : token
        ? _getPlatformAdminOverrideTenantId()
        : getTenantIdForRequest()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-ID': tenantId }),
    ...(assumedRoleId && { 'X-Assume-Role-Id': assumedRoleId }),
    ...(customHeaders as Record<string, string>),
  }

  if (restOptions.body instanceof FormData) {
    delete headers['Content-Type']
  }

  const config: RequestInit = {
    ...restOptions,
    headers,
    // Send opaque tagp-session cookie (Azure tokens stay in Redis; never in cookie).
    credentials: restOptions.credentials ?? 'include',
  }

  // If retry is disabled, make request directly
  if (!shouldRetry) {
    return makeRequest<T>(url, config, retryOn401)
  }

  // Use retry logic for network errors, transient 5xx, and 429 (Retry-After)
  return retryWithBackoff(
    async () => {
      return makeRequest<T>(url, config, retryOn401)
    },
    {
      maxRetries: 3,
      initialDelay: 1000,
      maxDelay: 60000,
      backoffMultiplier: 2,
      delayMs: (error, _attempt, currentDelay) => {
        return getRateLimitRetryDelayMs(error) ?? currentDelay
      },
      retryable: (error) => {
        // Don't retry if request was aborted
        if (error?.name === 'AbortError') {
          return false
        }

        // Use improved retry logic that checks error codes
        return isRetryableError(error)
      },
    }
  )
}
